import React, { createContext, useState, useEffect } from "react";
import translations from "./translation";

// Helper function to detect browser language
const getBrowserLanguage = () => {
    // Get the user's preferred language
    const browserLang = navigator.language || navigator.languages[0];

    // Get supported languages from your translations object
    const supportedLanguages = Object.keys(translations);

    // First, try to match the full language code (e.g., 'zh-CN', 'zh-TW')
    if (supportedLanguages.includes(browserLang)) {
        return browserLang;
    }

    // If full match fails, handle Chinese language variants specifically
    if (browserLang.startsWith('zh')) {
        // Check for specific Chinese variants
        if (browserLang.includes('CN') || browserLang.includes('Hans') || browserLang.includes('Simplified')) {
            return 'zh-CN';
        }
        if (browserLang.includes('TW') || browserLang.includes('Hant') || browserLang.includes('Traditional')) {
            return 'zh-TW';
        }
        // Default Chinese variant (you can adjust this based on your preference)
        return 'zh-TW';
    }

    // Handle Japanese language variants
    if (browserLang.startsWith('ja')) {
        return 'ja'; // Japanese is typically just 'ja' regardless of region
    }

    // For other languages, extract just the language code (e.g., 'en' from 'en-US')
    const langCode = browserLang.split('-')[0];

    // Return the detected language if supported, otherwise default to English
    return supportedLanguages.includes(langCode) ? langCode : 'en';
};

export const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    const [language, setLanguageState] = useState("en");

    // Initialize language on component mount
    useEffect(() => {
        // Check if user has previously set a language preference
        const savedLanguage = localStorage.getItem('userLanguage');

        if (savedLanguage) {
            // Use saved preference
            setLanguageState(savedLanguage);
        } else {
            // Detect browser language and set it
            const detectedLanguage = getBrowserLanguage();
            setLanguageState(detectedLanguage);
            localStorage.setItem('userLanguage', detectedLanguage);
        }
    }, []);

    // Function to change language (saves to localStorage)
    const setLanguage = (newLanguage) => {
        setLanguageState(newLanguage);
        localStorage.setItem('userLanguage', newLanguage);
    };

    const t = (key) => translations[language][key] || key;

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};